import * as R from 'ramda';
import React, {useState} from 'react';
import {withRouter} from 'react-router-dom';
import {of} from 'rxjs';
import {tap} from 'rxjs/operators';

import {useRoutes} from 'iss/hooks/useRoutes';
import {translate} from 'iss/strings';
import {createScale, getScale, gradingScales, modifyScale, subscribeToGradingScale} from 'iss/service/GradingScaleService';
import {hasAdminRole} from 'iss/service/PartyService';
import {searchForms} from 'iss/service/FormService';
import styles from 'iss/styles/GradingScaleEditor.styl';
import inputStyles from 'iss/styles/Input.styl';
import {swapDown, swapUp} from 'iss/utils/functional';
import BootstrapSelect from 'iss/widgets/bootstrap/Select';
import {Modal} from 'iss/widgets/bootstrap/Modal';
import {toOptions} from 'iss/widgets/Selectors';
import {Button, CancelButton, RemoveButton, SubmitButton, NewButton} from 'iss/widgets/Buttons';
import {FormSection, FormWithState, Input, Select, ValidationErrorBox} from 'iss/widgets/Forms';
import {translateFormEvaluationContext} from 'iss/widgets/domainTranslations';
import {PagedSearchResults, SearchResultScroller, ShowAllResultsButton, TextSearchBar, formatResultCount} from 'iss/widgets/PagedSearchResults';
import {RestResultMessage, WarningBox} from 'iss/widgets/MessageBox';
import {BatchOpRunnerDialog} from 'iss/widgets/batchOps/BatchOpRunnerDialog';
import {getChangeFormGradingScalesForScale} from 'iss/widgets/batchOps/evaluationForm/changeFormGradingScales';
import validators, {isHexColor, validatorFrom} from 'iss/widgets/form/validation/validators';
import {FlexItem, FlexRow} from 'iss/widgets/Flex';
import { switchMap } from 'rxjs/operators';

const GradingScaleEditor = withRouter(subscribeToGradingScale(function (props) {
  const {scale: {defaultGrade, ...initialScale}} = props;
  const [currentScale, setCurrentScale] = useState({
    ...initialScale,
    defaultGradeId: defaultGrade ? defaultGrade.id : null,
  });

  const isNew = () => R.isNil(currentScale.id);
  const isMobifiable = () => isNew() || currentScale.modifiable;

  const [relatedForms] = useState(R.map(R.assoc('isRelated', true), props.relatedForms));
  const [backendStatus, setBackendStatus] = useState(null);
  const [addedForms, setAddedForms] = useState([]);
  const [removedForms, setRemovedForms] = useState([]);
  const [formsToChange, setFormsToChange] = useState(isNew() ? relatedForms : []);
  const [batchOp, setBatchOp] = useState(null);
  const [showCancellationModal, setShowCancellationModal] = useState(false);
  const toggleCancellationModal = () => setShowCancellationModal(!showCancellationModal);

  const {history} = useRoutes();

  const onSubmit = (props) => {
    setBackendStatus(null);
    const scaleObservable = isMobifiable() ?
                              saveScale(props).pipe(
                                switchMap(savedScale => {
                                  setBackendStatus({message: translate('gradingScaleEditor.saveScaleSuccess')});
                                  // Reload saved scale data to get IDs for added grades.
                                  return getScale(savedScale.id).pipe(
                                    tap(scale => setCurrentScale(scale))
                                  );
                                })
                              )
                              : of(currentScale);
    scaleObservable.subscribe(
      scale => {
        if (!R.isEmpty(formsToChange)) {
          setBatchOp(() => getChangeFormGradingScalesForScale(scale));
        } else {
          reloadPage(scale.id);
        }
      },
      error => {
        setBackendStatus(error);
      }
    );
  };

  const saveScale = ({defaultGradeId, grades, ...scale}) => {
    const defaultGrade = R.find(R.propEq(defaultGradeId, "id"), grades);
    const data = {
      ...R.omit(['newFromOld'], scale),
      defaultGrade: isNew() && !!defaultGrade ? R.omit(['id'], defaultGrade) : defaultGrade,
      grades: grades.map((grade, index) => R.assoc('sortOrder', index, grade)).map(grade => ({
        ...grade,
        id: isNew() ? null : grade.id,
        color: grade.color === '' ? null : grade.color,
      }))
    };

    const saveScale = isNew() ? createScale : modifyScale;
    return saveScale(data);
  };

  const updateFormsToChange = (isRelated, form, isSelected) => {
    // The changeset for new scale is the related set + added - removed.
    // For old one, it is just the added ones (since removals are not allowed).
    if (isNew()) {
      // Selecting a form, no matter if it is from related or non-related set, makes it belong to the changeset.
      if (isSelected) {
        setFormsToChange(R.append(form, formsToChange));
      } else {
        setFormsToChange(R.reject(R.propEq(form.id, 'id'), formsToChange));
      }
    } else if (!isRelated) {
      // Selecting from non-related set makes it belong to the changeset.
      if (isSelected) {
        setFormsToChange(R.append(form, formsToChange));
      } else {
        setFormsToChange(R.reject(R.propEq(form.id, 'id'), formsToChange));
      }
    }
  };

  const onChangeSelected = (form, isSelected) => {
    const relatedMatch = relatedForms.find(related => related.id === form.id);
    if (relatedMatch) {
      if (isSelected) {
        setRemovedForms(R.reject(R.propEq(relatedMatch.id, 'id'), removedForms));
      } else {
        setRemovedForms(R.append(relatedMatch, removedForms));
      }
      updateFormsToChange(true, relatedMatch, isSelected);
    } else if (!relatedMatch) {
      if (isSelected) {
        setAddedForms(R.append(form, addedForms));
      } else {
        setAddedForms(R.reject(R.propEq(form.id, 'id'), addedForms));
      }
      updateFormsToChange(false, form, isSelected);
    }
  };

  const reloadPage = (newScaleId) => history.push("/scales/" + R.defaultTo(currentScale.id, newScaleId));

  const onCancellationDialogClose = () => {
    setBatchOp(null);
    reloadPage();
  };

  return <>
    <FormWithState initialValues={currentScale} className="tight-form-controls" onSubmit={onSubmit}>
      <FlexRow alignItems="center" flexAllChildren>
        <FlexItem>
          <h1>{translate(isNew() ? 'gradingScaleEditor.newScaleTitle' : 'gradingScaleEditor.editScaleTitle')}</h1>
        </FlexItem>
        <FlexItem flex="none">
          {
            !isNew() && hasAdminRole()
              && <NewButton id="new-gradingScale" link={"/scales/new-from/" + currentScale.id} text={translate('gradingScaleEditor.newScaleFromThis')} />
          }
        </FlexItem>
      </FlexRow>

      <Input disabled={!isMobifiable()} label="gradingScaleEditor.scaleName" id="scale-name" valuePath="name" validate={validators.required} maxLength="255" showCounter={true} />
      <Select disabled={!isMobifiable()} id="gradeReportingStyle" label="gradingScaleEditor.gradeReportingStyle" clearable={false} options={[
        {value: 'NAME_AND_NUMBER_VALUE', label: translate('gradeReportingStyle.name_and_number_value')},
        {value: 'NAME_ONLY', label: translate('gradeReportingStyle.name_only')},
      ]} valuePath="gradeReportingStyle" />
      <GradesEditor modifiable={isMobifiable()}/>
      <DefaultGradeChooser modifiable={isMobifiable()} />
      <ValidationErrorBox id="gradingScaleValidation" validate={[
        validatorFrom(scale => scale.grades.length >= 2, "gradingScaleEditor.shouldHaveManyGrades"),
      ]} />
      <FormPickerTable
        pickForNewScale={isNew()}
        relatedForms={relatedForms}
        addedForms={addedForms}
        removedForms={removedForms}
        onChangeSelected={onChangeSelected}
      />
      <RestResultMessage id="backendError" result={backendStatus} />
      <div className="form-group">
        <SubmitButton id="save-scale" text={translate('common.save')} />
        <CancelButton link="/scales" />
      </div>
    </FormWithState>
    <BatchOpRunnerDialog operation={batchOp} operationTargets={formsToChange}
      onClose={toggleCancellationModal}
      onOperationDone={() => {
        gradingScales.clearCache(); // Required to refresh isModifiable property in cached scales after switching form scales
        reloadPage();
      }}
    />
    <Modal
      title={translate('gradingScaleEditor.changeFormGradingScales.cancellation.title')}
      visible={showCancellationModal}
      size="medium"
      onClose={() => {toggleCancellationModal(); onCancellationDialogClose();}}
    >
      <div className={styles.gradingScaleChangeModal}>
        <WarningBox message={translate('gradingScaleEditor.changeFormGradingScales.cancellation.body')} />
        <Button buttonLook="primary" onClick={onCancellationDialogClose}>{'OK'}</Button>
      </div>
    </Modal>
  </>;
}));

function GradesEditor({modifiable}) {
  return <FormSection valuePath="grades" pure={true}>
    {(grades, formApi) =>
        <div className="clearfix">
          <h2>{translate('gradingScaleEditor.grades')}</h2>
          <table id="grades" className="table table-hover">
            <thead>
              <tr>
                <th>{translate('gradingScaleEditor.scaleName')}</th>
                <th>{translate('gradingScaleEditor.scaleNumericValue')}</th>
                <th>{translate('gradingScaleEditor.gradeColor')}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {grades.map((grade, index) =>
                <GradeRow key={index} gradeNumber={index} gradeCount={grades.length} modifiable={modifiable} gradeUpdater={(updater) => () => formApi.updateValue([], updater)} />
              )}
            </tbody>
          </table>
          <Button id="new-grade" disabled={!modifiable} className="pull-right"
            onClick={() => formApi.updateValue([], R.append(getEmptyGrade()))}>
            {translate('gradingScaleEditor.newGrade')}
          </Button>
        </div>
    }
  </FormSection>;
}

let idCounter = 0;
function getEmptyGrade() {
  //Empty grades have a negative id so the default grade selector can address them by id
  return {
    id: --idCounter,
    text: "",
    numericValue: 0,
    color: 'FFFFFF'
  };
}

function GradeRow({gradeNumber, gradeCount, modifiable, gradeUpdater}) {
  const index = gradeNumber;
  const isFirst = index == 0;
  const isLast = index == gradeCount-1;

  return <FormSection valuePath={index}>
    {(grade) =>
      <tr className="grade">
        <td>
          <Input type="text" disabled={!modifiable} id={"grade-name-"+index} valuePath="text" validate={validators.required} maxLength="50" showCounter={true}/>
        </td>
        <td>
          <Input type="number" disabled={!modifiable} id={"grade-value-"+index} valuePath="numericValue" validate={validators.integer}  />
        </td>
        <td>
          <ColorPreview color={grade.color} />
          <Input className={styles.colorInput} type="text" size="6" maxLength="6" disabled={!modifiable}
            placeholder="FFFFFF" id={"grade-color-"+index} valuePath="color" validate={validators.hexColor} />
        </td>
        <td>
          <Button id={"move-grade-"+index+"-up"} size="sm" disabled={isFirst||!modifiable}
            icon="arrow-up" onClick={gradeUpdater(swapDown(index))} />
          <Button id={"move-grade-"+index+"-down"} size="sm" disabled={isLast||!modifiable}
            icon="arrow-down" onClick={gradeUpdater(swapUp(index))} />
          <RemoveButton id={"remove-grade-"+index} size="sm" disabled={!modifiable}
            onClick={gradeUpdater(R.remove(index, 1))} />
        </td>
      </tr>
    }
  </FormSection>;
}

function FormPickerTable({relatedForms, addedForms, removedForms, onChangeSelected, pickForNewScale}) {
  const [search, setSearch] = useState({query: "", context: 'QUALITY_ASSURANCE'});

  const selected = R.differenceWith(equalsById, relatedForms.concat(addedForms), removedForms);
  const selectedIds = selected.map(form => form.id);
  const markSelected = (forms) => forms.map(
    form => ({...form, isSelected: selectedIds.includes(form.id)})
  );

  return <React.Fragment>
    <h2>{translate('gradingScaleEditor.formsUsingScale')}</h2>
      <b>{translate('gradingScaleEditor.searchForForms')}</b>
      <FormPickerSearch search={search} onChange={setSearch} className={styles.formSearch}/>
      <div className={styles.formSearchResults}>
        <PagedSearchResults getItems={searchForms} search={search}>
          {(results, resultApi) => {
            const idsInResults = results.results.map(form => form.id);
            const filterByResults = (list) => (R.isNil(search) || R.isEmpty(search)) ? list : list.filter(form => idsInResults.includes(form.id));
            return <SearchResultScroller
              searchResult={results}
              onLoadMore={resultApi.loadMoreItems}
              hasMaxHeight={true}
            >
              <FlexRow alignItems="center" flexAllChildren>
                <FlexItem>
                  {formatResultCount(results)}
                  <ShowAllResultsButton searchResult={results} onShowAll={resultApi.loadAllItems} />
                </FlexItem>
              </FlexRow>
              <table className="table table-striped" id="forms">
                <thead>
                  <tr>
                    <th></th>
                    <th>{translate('formList.formName')}</th>
                    <th>{translate('formList.formDescription')}</th>
                  </tr>
                </thead>
                <tbody>
                  {
                    R.defaultTo([], sortByIsSelectedAndName(markSelected(filterByResults(relatedForms))))
                      .map((form) => <FormPickerRow
                                      key={form.id} form={form}
                                      isSelected={form.isSelected}
                                      onChangeSelected={onChangeSelected}
                                      deselectRelatedAllowed={pickForNewScale}
                                    />)
                  }
                  <tr>
                    <td colSpan={3} className={styles.resultsDivider}>
                      {translate('gradingScaleEditor.' + (pickForNewScale ? 'formListDivider' : 'formListDividerEditingOld'))}
                    </td>
                  </tr>
                  {
                    R.defaultTo([], filterByResults(addedForms))
                      .map((form) => <FormPickerRow
                                        key={form.id} form={form}
                                        isSelected={true}
                                        onChangeSelected={onChangeSelected}
                                        deselectRelatedAllowed={pickForNewScale}
                                      />)
                  }
                  {
                    R.defaultTo([], R.differenceWith(equalsById, results.results, filterByResults(relatedForms.concat(addedForms))))
                      .map((form) => <FormPickerRow
                                        key={form.id} form={form}
                                        isSelected={false}
                                        onChangeSelected={onChangeSelected}
                                        deselectRelatedAllowed={pickForNewScale}
                                      />)
                }
                </tbody>
              </table>
            </SearchResultScroller>;
          }}
        </PagedSearchResults>
      </div>
  </React.Fragment>;
}

function FormPickerSearch({search, onChange}) {
  const {query, context} = search;
  const options = toOptions(
    translateFormEvaluationContext,
    ['QUALITY_ASSURANCE', 'WORK_SECURITY', 'OTHER']
  );

  return <TextSearchBar id="form-search" search={query}
    onChange={(newQuery) => onChange({context, query: newQuery})}>
    <BootstrapSelect id="formEvaluationContext"
      options={options} value={context} clearable={false}
      className={styles.formSearchContextSelector}
      onChange={({value}) => onChange({query, context: value})}

    />
  </TextSearchBar>;
}

function FormPickerRow({form, isSelected, onChangeSelected, deselectRelatedAllowed}) {
  const isDisabled = form.isRelated && isSelected && !deselectRelatedAllowed;
  return <tr data-form-id={form.id}>
    <td>
      <input
        type="checkbox" id={"form-"+form.id}
        className={inputStyles.largeCheckbox}
        checked={isSelected} disabled={isDisabled}
        onChange={() => {onChangeSelected(form, !isSelected);}}
      />
    </td>
    <td>
      <span>{form.name}</span>
    </td>
    <td>
      <span>{form.description}</span>
    </td>
  </tr>;
}

const equalsById = (a, b) => !!a && !!b && a.id === b.id;
const sortByIsSelectedAndName = (forms) => R.sortWith([
  R.descend(R.prop('isSelected')),
  R.ascend(R.compose(R.toLower, R.prop('name')))
], forms);


function ColorPreview({color}) {
  const isValid = isHexColor(color) && color.length > 0;
  const style = {
    backgroundColor: isValid ? "#"+color : "transparent",
    color: isValid ? (isBright(color) ? 'black' : 'white') : '550000',
    width: "35px"
  };
  const props = { size: "sm", style };
  return isValid ? <Button icon="ok" {...props} alt={translate('gradingScaleEditor.gradeColor')} /> :
    <Button icon="remove" buttonLook="link" {...props} alt={translate("gradingScaleEditor.invalidColor")} />;
}

const isBright = R.pipe(
  R.splitEvery(2),
  R.map(i => parseInt(i, 16)),
  R.apply((r,g,b) => (r * 0.299 + g * 0.587 + b * 0.164) > 186)
);

function DefaultGradeChooser({modifiable}) {
  return <FormSection pure={true}>
    {scale => {
      const scaleOptions = R.prepend(
        {
          value: null,
          label: translate('gradingScaleEditor.chooseDefaultGrade'),
        }, R.defaultTo([], R.defaultTo([], scale).grades).map(grade => ({
          value: grade.id,
          label: `${grade.text}`
        }))
      );

      return <div id="default-grade-container" className="clearfix">
        <Select id="default-grade" label="gradingScaleEditor.defaultGrade" valuePath="defaultGradeId"
          options={scaleOptions} disabled={!modifiable} resetValue={{value:null}} />
      </div>;
    }}
  </FormSection>;
}

export default GradingScaleEditor;



// WEBPACK FOOTER //
// ./modules/issFrontend/pages/GradingScaleEditor.jsx